/**
 * Gemini Live native-audio voice UI (parallel to VoiceConversationEngine; legacy unchanged).
 */

import { tokens } from '@healthdesk/shared-utils';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PersonIcon from '@mui/icons-material/Person';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';

import useGeminiLiveVoiceConversationEngine from './useGeminiLiveVoiceConversationEngine';
import useAudioLevel from './useAudioLevel';
import VoiceLevelIndicator from './VoiceLevelIndicator';
import ScmapPreflightGate from './ScmapPreflightGate';
import {
  SUPPORTED_LANGUAGES,
  getLanguageByCode,
} from '../../features/patient/components/symptomCheck/voice-conversation';

function GeminiLiveVoiceConversationEngine({
  onPauseDetected,
  title = 'Voice Conversation',
  openingMessage = 'Hello! How can I help you today?',
  maxQuestions = 0,
  language = 'en-US',
  onLanguageChange,
  compact = false,
  showLanguageSelector = false,
  onComplete,
  onStartRequested,
  onSkip,
  context,
  onUserSpeech,
  onLogEntry,
  onLiveTranscript,
  autoStart = false,
  askFirstQuestionAfterOpening = false,
  askDisclaimerAfterOpening = true,
  /** Called when disclaimer is folded into the opening turn (Gemini Live). */
  onDisclaimerDelivered,
  /** Gemini Live prebuilt voice name (symptom checker: from voice settings). */
  liveSpeechVoice,
  /** When false, hides the "Gemini Live (beta)" status chip in the header. */
  showBackendChip = true,
  headerExtra,
  beforeStart,
  conversationLog: controlledConversationLog,
  onConversationLogChange,
  questionsAsked: controlledQuestionsAsked,
  onQuestionsAskedChange,
  onEngineError,
  /** When true, runs mic/speaker preflight in parallel with Live connection on Start Live. */
  enablePreflight = false,
  /** Page-scoped ref; when current === true, preflight is skipped. */
  preflightCompletedRef,
  /** Fires when the Start Live pre-start row is shown or hidden. */
  onPreStartControlsVisibleChange,
}) {
  const [preflightVisible, setPreflightVisible] = useState(false);
  const preflightResolverRef = useRef(null);

  const languageLabelId = useId();
  const engine = useGeminiLiveVoiceConversationEngine({
    onPauseDetected,
    openingMessage,
    language,
    context,
    onComplete,
    onUserSpeech,
    onLogEntry,
    onLiveTranscript,
    askFirstQuestionAfterOpening,
    askDisclaimerAfterOpening,
    onDisclaimerDelivered,
    liveSpeechVoice,
    controlledConversationLog,
    onConversationLogChange,
    controlledQuestionsAsked,
    onQuestionsAskedChange,
  });

  const {
    isActive,
    isStarting,
    isListening,
    isSpeaking,
    questionsAsked,
    conversationLog,
    audioConfirmed,
    isAcceptingInput,
    speechSupported,
    error,
    startConversation,
    prepareLiveConnection,
    beginLiveAudio,
    stopConversation,
    endConversationAcknowledged,
    conversationScrollRef,
  } = engine;

  const getPreflightPromise = useCallback(() => {
    if (preflightCompletedRef?.current === true) {
      return Promise.resolve();
    }
    setPreflightVisible(true);
    return new Promise((resolve) => {
      preflightResolverRef.current = resolve;
    });
  }, [preflightCompletedRef]);

  const handlePreflightComplete = useCallback(() => {
    if (preflightCompletedRef) {
      preflightCompletedRef.current = true;
    }
    preflightResolverRef.current?.();
    preflightResolverRef.current = null;
    setPreflightVisible(false);
  }, [preflightCompletedRef]);

  const lastAssistantLine = [...conversationLog].reverse().find((e) => e.speaker === 'assistant');
  const currentQuestion = lastAssistantLine?.text || '';

  const audioLevel = useAudioLevel(
    isActive && isListening && audioConfirmed && isAcceptingInput && !isSpeaking && !preflightVisible
  );

  useEffect(() => {
    if (autoStart && !isActive) {
      const canStart = typeof onStartRequested === 'function' ? onStartRequested() : true;
      if (canStart !== false) startConversation();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error && onEngineError) onEngineError(error);
  }, [error, onEngineError]);

  const handleStartClick = useCallback(async () => {
    const canStart = typeof onStartRequested === 'function' ? onStartRequested() : true;
    if (canStart === false) return;

    if (!enablePreflight) {
      startConversation();
      return;
    }

    const connectionPromise = prepareLiveConnection().catch((err) => {
      setPreflightVisible(false);
      throw err;
    });
    const preflightPromise = getPreflightPromise();

    try {
      await Promise.all([connectionPromise, preflightPromise]);
      setPreflightVisible(false);
      await beginLiveAudio();
    } catch (startErr) {
      console.warn('[GeminiLive] Start Live with preflight failed:', startErr);
      setPreflightVisible(false);
    }
  }, [
    onStartRequested,
    enablePreflight,
    startConversation,
    prepareLiveConnection,
    beginLiveAudio,
    getPreflightPromise,
  ]);

  useEffect(() => {
    if (conversationScrollRef.current) {
      requestAnimationFrame(() => {
        if (conversationScrollRef.current) {
          conversationScrollRef.current.scrollTop = conversationScrollRef.current.scrollHeight;
        }
      });
    }
  }, [conversationLog, conversationScrollRef]);

  const showPreflightGate =
    enablePreflight && preflightVisible && conversationLog.length === 0;
  const showPreStartControls =
    speechSupported &&
    !isActive &&
    conversationLog.length === 0 &&
    !showPreflightGate;

  useEffect(() => {
    onPreStartControlsVisibleChange?.(showPreStartControls);
  }, [showPreStartControls, onPreStartControlsVisibleChange]);

  if (!speechSupported) {
    return (
      <Box sx={{ p: compact ? 2 : 3, textAlign: 'center', bgcolor: 'warning.lighter', borderRadius: 2 }}>
        <Typography color="warning.dark" variant={compact ? 'body2' : 'body1'}>
          Live voice needs microphone access and a supported browser (Chrome, Edge, or Safari).
        </Typography>
      </Box>
    );
  }

  const progress = maxQuestions > 0 ? Math.min(100, (questionsAsked.length / maxQuestions) * 100) : 0;
  const showConnectingDuringPreflight =
    showPreflightGate && (isStarting || (isListening && !audioConfirmed));

  const content = (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={compact ? 1 : 2}>
        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={600}>
          {title}
        </Typography>
        {isActive && !showPreflightGate && (
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {showBackendChip !== false && (
              // FUTURE: deprecate — replaced by switch-level chip
              <Chip icon={<GraphicEqIcon />} label="Gemini Live (beta)" color="secondary" size="small" variant="outlined" />
            )}
            {isStarting && !isSpeaking && (
              <Chip
                icon={<VolumeUpIcon />}
                label="Connecting..."
                color="info"
                size="small"
                sx={{ animation: 'pulse 0.8s infinite' }}
              />
            )}
            {isListening && audioConfirmed && isAcceptingInput && !isStarting && (
              <Chip
                icon={<MicIcon />}
                label={compact ? 'Live' : 'Listening (Live)'}
                color="success"
                size="small"
                sx={{ animation: 'pulse 1.5s infinite' }}
              />
            )}
            {isSpeaking && (
              <Chip icon={<VolumeUpIcon />} label={compact ? 'Speaking' : 'Speaking...'} color="primary" size="small" />
            )}
          </Box>
        )}
      </Box>

      {headerExtra}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isActive && !showPreflightGate && (
        <Box sx={{ mb: compact ? 1 : 2 }}>
          <LinearProgress
            variant={isStarting || isSpeaking || (isListening && !audioConfirmed) ? 'indeterminate' : 'determinate'}
            value={
              maxQuestions > 0 && isListening && audioConfirmed && !isStarting
                ? progress
                : isListening && audioConfirmed && !isStarting
                  ? 100
                  : 0
            }
            color={isStarting ? 'info' : isSpeaking ? 'primary' : isListening && audioConfirmed ? 'success' : 'warning'}
            sx={{ height: compact ? 3 : 4, borderRadius: 2 }}
          />
          {maxQuestions > 0 && isListening && audioConfirmed && !isStarting && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
              Question {Math.min(questionsAsked.length, maxQuestions)} of {maxQuestions}
            </Typography>
          )}
        </Box>
      )}

      {isActive && !isStarting && !showPreflightGate && audioConfirmed && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: compact ? 1 : 1.5 }}>
          <VoiceLevelIndicator
            level={audioLevel}
            muted={isSpeaking || !isAcceptingInput}
            height={compact ? 22 : 28}
            userHasSpoken={conversationLog.some((e) => e.speaker === 'user')}
          />
        </Box>
      )}

      {conversationLog.length > 0 && (
        <Box
          ref={conversationScrollRef}
          sx={{
            maxHeight: compact ? 150 : 200,
            overflow: 'auto',
            mb: compact ? 1 : 2,
            p: compact ? 1 : 1.5,
            bgcolor: 'background.paper',
            borderRadius: 2,
            flex: compact ? 1 : 'none',
          }}
        >
          {conversationLog.map((entry, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1,
                opacity: index === conversationLog.length - 1 ? 1 : 0.7,
              }}
            >
              {entry.speaker === 'user' ? (
                <PersonIcon fontSize="small" color="action" />
              ) : (
                <VolumeUpIcon fontSize="small" color="primary" />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontStyle: entry.speaker === 'assistant' ? 'italic' : 'normal',
                  color: entry.speaker === 'assistant' ? 'primary.main' : 'text.primary',
                }}
              >
                {entry.text}
              </Typography>
            </Box>
          ))}
          {!isActive && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
              Conversation has ended.
            </Typography>
          )}
        </Box>
      )}

      <Fade in={!!currentQuestion && isActive}>
        <Box sx={{ mb: compact ? 1 : 2, p: compact ? 1.5 : 2, bgcolor: 'secondary.lighter', borderRadius: 2 }}>
          <Typography variant={compact ? 'body2' : 'body1'} color="secondary.dark" fontWeight={500}>
            &quot;{currentQuestion}&quot;
          </Typography>
        </Box>
      </Fade>

      {showPreStartControls && beforeStart}

      {showPreflightGate && (
        <Box sx={{ mb: 2, width: '100%' }}>
          <ScmapPreflightGate onComplete={handlePreflightComplete} />
          {showConnectingDuringPreflight && (
            <Box sx={{ opacity: 0.7, mt: 2, width: '100%' }}>
              <Box display="flex" justifyContent="center" mb={1}>
                <Chip
                  icon={<VolumeUpIcon />}
                  label="Connecting..."
                  color="info"
                  size="small"
                  sx={{ animation: 'pulse 0.8s infinite' }}
                />
              </Box>
              <LinearProgress
                variant="indeterminate"
                color="info"
                sx={{ height: compact ? 3 : 4, borderRadius: 2 }}
              />
            </Box>
          )}
        </Box>
      )}

      {showPreStartControls && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          {showLanguageSelector ? (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id={languageLabelId}>Language</InputLabel>
              <Select
                labelId={languageLabelId}
                value={language}
                label="Language"
                onChange={(e) => onLanguageChange?.(e.target.value)}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {getLanguageByCode(language)?.label || language} — applies to this Live session
            </Typography>
          )}
          <Button
            variant="contained"
            size={compact ? 'medium' : 'large'}
            startIcon={<MicIcon />}
            onClick={handleStartClick}
            sx={{
              px: compact ? 3 : 4,
              py: compact ? 1 : 1.5,
              borderRadius: 3,
              fontSize: compact ? '0.95rem' : '1.1rem',
            }}
          >
            {compact ? 'Start Live' : 'Start Live Voice'}
          </Button>
        </Box>
      )}

      <Box display="flex" justifyContent="center" gap={2} sx={{ mt: compact ? 1 : 0 }}>
        {isActive &&
          (isAcceptingInput || endConversationAcknowledged) &&
          !isStarting &&
          !showPreflightGate &&
          audioConfirmed && (
          <Button
            variant="contained"
            size={compact ? 'medium' : 'large'}
            startIcon={<StopIcon />}
            onClick={stopConversation}
            aria-pressed={endConversationAcknowledged}
            sx={{
              px: compact ? 3 : 4,
              py: compact ? 1 : 1.5,
              borderRadius: 3,
              bgcolor: tokens.colors.msiRed.light,
              '&:hover': { bgcolor: '#6b1538' },
              ...(endConversationAcknowledged && {
                boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.35)',
                transform: 'translateY(1px)',
              }),
            }}
          >
            End Conversation
          </Button>
        )}
        {onSkip && showPreStartControls && (
          <Button
            variant="outlined"
            color="inherit"
            size={compact ? 'medium' : 'large'}
            onClick={onSkip}
            sx={{ px: compact ? 3 : 4, py: compact ? 1 : 1.5, borderRadius: 3 }}
          >
            Skip
          </Button>
        )}
      </Box>

      {showPreStartControls && !compact && (
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 2 }}>
          Low-latency voice via Gemini Live (beta). Speak naturally; the assistant uses your scripted flow through a
          secure tool bridge.
        </Typography>
      )}

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.6; }
          100% { opacity: 1; }
        }
      `}</style>
    </>
  );

  if (compact) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {content}
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        ...contentCardSx,
        p: 3,
        background: isActive
          ? 'linear-gradient(135deg, #ede7f6 0%, #e8f5e9 100%)'
          : 'background.paper',
        transition: 'background 0.3s ease',
      }}
    >
      {content}
    </Paper>
  );
}

export default GeminiLiveVoiceConversationEngine;
